import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Sparkles, AlertTriangle, AlertCircle, CheckCircle2, ChevronRight, Loader2,
  Settings, ShoppingCart, Package, Factory, Workflow, Eye, X, Search,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

type Modo = 'produtos' | 'materia_prima' | 'mrp'
type Urg = 'critico' | 'alerta' | 'sem_consumo' | 'ok'

interface FornecedorSug {
  fornecedor_id: number
  fornecedor_nome: string | null
  lead_time_dias: number
  preco_ultima_compra: number
  ultima_compra_at: string | null
}

interface SugestaoLinha {
  tipo: 'produto' | 'materia_prima'
  modo?: 'mrp'
  id: number
  codigo: string
  descricao: string
  unidade: string
  categoria?: string | null
  estoque_atual: number
  estoque_minimo: number
  estoque_minimo_estimado?: boolean
  consumo_diario: number
  consumo_mensal: number
  cobertura_dias: number | null
  lead_time_dias: number
  ponto_pedido: number
  urgencia: Urg
  quantidade_sugerida: number
  preco_estimado: number
  valor_estimado: number
  demanda_horizonte?: number
  horizonte_dias?: number
  contribuicoes?: { produto_id: number; vendas_diarias: number; qty_por_unidade: number; demanda_horizonte: number }[]
  fornecedor_sugerido: FornecedorSug | null
}

interface KPIs {
  total_itens: number
  criticos: number
  alertas: number
  ok: number
  sem_consumo: number
  valor_total_sugerido: number
}

interface Parametros {
  janela_consumo_dias: number
  cobertura_alvo_dias: number
  buffer_lead_time_dias: number
  limite_aprovacao_bpm: number | string
  processo_aprovacao_id: number | null
}

const URG_BADGE: Record<Urg, { cls: string; label: string; icon: any }> = {
  critico: { cls: 'bg-rose-100 text-rose-800 border-rose-300', label: 'CRÍTICO', icon: AlertTriangle },
  alerta: { cls: 'bg-amber-100 text-amber-800 border-amber-300', label: 'Alerta', icon: AlertCircle },
  sem_consumo: { cls: 'bg-ink-100 text-ink-600 border-ink-300', label: 'Sem giro', icon: Package },
  ok: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'OK', icon: CheckCircle2 },
}

const MODOS: { id: Modo; label: string; icon: any; desc: string }[] = [
  { id: 'produtos', label: 'Produtos (revenda)', icon: ShoppingCart, desc: 'Só produtos de revenda — sugestão pelo giro de vendas + estoque mínimo' },
  { id: 'materia_prima', label: 'Matérias-primas', icon: Package, desc: 'Matérias-primas do cadastro — sugestão pelo estoque mínimo (e consumo, se houver)' },
  { id: 'mrp', label: 'MRP por fórmula', icon: Workflow, desc: 'Projeta demanda de MP a partir das fichas técnicas dos produtos finais' },
]

export default function SugestoesCompraPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [modo, setModo] = useState<Modo>('produtos')
  const [busca, setBusca] = useState('')
  const [filtroUrg, setFiltroUrg] = useState<string>('') // '' = todas alertas, 'critico', 'alerta', 'sem_consumo'
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [showParams, setShowParams] = useState(false)
  const [showDetalhe, setShowDetalhe] = useState<SugestaoLinha | null>(null)

  const linhas = useQuery<SugestaoLinha[]>({
    queryKey: ['compras-sugestoes', modo],
    queryFn: () => api.get(`/compras/sugestoes?modo=${modo}&apenas_alertas=false`).then(r => r.data),
  })

  const kpis = useQuery<KPIs>({
    queryKey: ['compras-sugestoes-kpis', modo],
    queryFn: () => api.get(`/compras/sugestoes/kpis?modo=${modo}`).then(r => r.data),
  })

  const params = useQuery<Parametros>({
    queryKey: ['compras-parametros'],
    queryFn: () => api.get('/compras/parametros').then(r => r.data),
  })

  const mutateGerar = useMutation({
    mutationFn: (payload: any) => api.post('/compras/sugestoes/gerar-pedidos', payload).then(r => r.data),
    onSuccess: (data) => {
      toast.success(`${data.total_pedidos} pedido(s) gerado(s) — total ${formatMoney(Number(data.valor_total))}`)
      setSelecionados(new Set())
      qc.invalidateQueries({ queryKey: ['compras-sugestoes', modo] })
      qc.invalidateQueries({ queryKey: ['compras-pedidos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Falha ao gerar pedidos'),
  })

  const filtradas = useMemo(() => {
    const all = linhas.data || []
    return all.filter(l => {
      if (filtroUrg === '' && l.urgencia === 'ok') return false
      if (filtroUrg && l.urgencia !== filtroUrg) return false
      if (busca) {
        const q = busca.toLowerCase()
        return l.codigo.toLowerCase().includes(q) || l.descricao.toLowerCase().includes(q)
      }
      return true
    })
  }, [linhas.data, busca, filtroUrg])

  const toggleSel = (id: number) => {
    setSelecionados(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const toggleSelAll = () => {
    const elegiveis = filtradas.filter(l => l.fornecedor_sugerido && l.quantidade_sugerida > 0).map(l => l.id)
    if (selecionados.size === elegiveis.length) setSelecionados(new Set())
    else setSelecionados(new Set(elegiveis))
  }

  const totalSelValor = useMemo(() => {
    return filtradas
      .filter(l => selecionados.has(l.id))
      .reduce((s, l) => s + l.valor_estimado, 0)
  }, [filtradas, selecionados])

  const gruposFornecedor = useMemo(() => {
    const sel = filtradas.filter(l => selecionados.has(l.id) && l.fornecedor_sugerido)
    const map: Record<number, { fornecedor_id: number; fornecedor_nome: string | null; itens: SugestaoLinha[] }> = {}
    for (const l of sel) {
      const fid = l.fornecedor_sugerido!.fornecedor_id
      if (!map[fid]) map[fid] = { fornecedor_id: fid, fornecedor_nome: l.fornecedor_sugerido!.fornecedor_nome, itens: [] }
      map[fid].itens.push(l)
    }
    return Object.values(map)
  }, [filtradas, selecionados])

  const onGerar = () => {
    if (selecionados.size === 0) return
    const itens = filtradas
      .filter(l => selecionados.has(l.id) && l.fornecedor_sugerido)
      .map(l => ({
        tipo: l.tipo,
        id: l.id,
        quantidade: l.quantidade_sugerida,
        preco_unitario: l.preco_estimado,
        fornecedor_id: l.fornecedor_sugerido!.fornecedor_id,
      }))
    if (!itens.length) {
      toast.error('Nenhuma linha elegível selecionada (precisa ter fornecedor sugerido)')
      return
    }
    if (!confirm(`Gerar ${gruposFornecedor.length} pedido(s) de compra agrupando por fornecedor — total ${formatMoney(totalSelValor)}?`)) return
    mutateGerar.mutate({ itens, previsao_entrega_dias: 7 })
  }

  const elegiveisCount = filtradas.filter(l => l.fornecedor_sugerido && l.quantidade_sugerida > 0).length

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-violet-600" />
            Sugestões de Compra
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Análise inteligente de giro, estoque e lead time para sugerir compras automáticas.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowParams(true)}
            className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50 text-sm flex items-center gap-2"
          >
            <Settings className="h-4 w-4" /> Parâmetros
          </button>
          <button
            onClick={() => nav('/compras')}
            className="px-3 py-2 rounded-lg border border-ink-300 hover:bg-ink-50 text-sm"
          >
            Pedidos de Compra
          </button>
        </div>
      </div>

      {/* Tabs de modo */}
      <div className="flex gap-2 flex-wrap">
        {MODOS.map(m => {
          const Icon = m.icon
          const ativo = modo === m.id
          return (
            <button
              key={m.id}
              onClick={() => { setModo(m.id); setSelecionados(new Set()) }}
              className={`px-4 py-2 rounded-lg border flex items-center gap-2 text-sm transition ${
                ativo
                  ? 'bg-violet-600 border-violet-600 text-white shadow'
                  : 'bg-white border-ink-300 text-ink-700 hover:bg-ink-50'
              }`}
              title={m.desc}
            >
              <Icon className="h-4 w-4" />
              {m.label}
            </button>
          )
        })}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Críticos" value={kpis.data?.criticos ?? '—'} cls="bg-rose-50 border-rose-200 text-rose-800" />
        <Kpi label="Alertas" value={kpis.data?.alertas ?? '—'} cls="bg-amber-50 border-amber-200 text-amber-800" />
        <Kpi label="Sem giro" value={kpis.data?.sem_consumo ?? '—'} cls="bg-ink-50 border-ink-200 text-ink-700" />
        <Kpi label="Total itens" value={kpis.data?.total_itens ?? '—'} cls="bg-sky-50 border-sky-200 text-sky-800" />
        <Kpi
          label="Valor sugerido"
          value={kpis.data ? formatMoney(kpis.data.valor_total_sugerido) : '—'}
          cls="bg-violet-50 border-violet-200 text-violet-800"
        />
      </div>

      {/* Aviso: MP sem estoque mínimo (não dá pra sugerir quantidade automática) */}
      {modo === 'materia_prima' && (() => {
        const semMin = (linhas.data || []).filter(l => !l.estoque_minimo).length
        const total = (linhas.data || []).length
        if (total === 0 || semMin === 0) return null
        return (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <b>{semMin} de {total} matérias-primas sem estoque mínimo.</b> Elas aparecem como "Sem giro" e ainda não geram
              quantidade sugerida. Defina o <b>estoque mínimo</b> no cadastro do produto (ou eu importo do WinThor) pra receber sugestão automática de compra.
            </div>
          </div>
        )
      })()}

      {/* Filtros + busca */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(filtradas, l => setShowDetalhe(l))}
            placeholder="Buscar código ou descrição..."
            className="w-full pl-9 pr-3 py-2 border border-ink-300 rounded-lg text-sm"
          />
        </div>
        <select
          value={filtroUrg}
          onChange={e => setFiltroUrg(e.target.value)}
          className="px-3 py-2 border border-ink-300 rounded-lg text-sm"
        >
          <option value="">Apenas alertas</option>
          <option value="critico">Só Críticos</option>
          <option value="alerta">Só Alertas</option>
          <option value="sem_consumo">Sem giro</option>
        </select>
        <div className="ml-auto flex items-center gap-3 text-sm text-ink-700">
          <span>{selecionados.size} selecionado(s) — {formatMoney(totalSelValor)}</span>
          <button
            onClick={onGerar}
            disabled={selecionados.size === 0 || mutateGerar.isPending}
            className="px-4 py-2 bg-violet-600 hover:bg-violet-700 disabled:bg-ink-300 text-white rounded-lg text-sm font-medium flex items-center gap-2"
          >
            {mutateGerar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            Gerar pedidos ({gruposFornecedor.length})
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 border-b border-ink-200">
              <tr>
                <th className="px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={elegiveisCount > 0 && selecionados.size === elegiveisCount}
                    onChange={toggleSelAll}
                  />
                </th>
                <th className="px-3 py-2 text-left">Urg</th>
                <th className="px-3 py-2 text-left">Código</th>
                <th className="px-3 py-2 text-left">Descrição</th>
                <th className="px-3 py-2 text-right">Estoque</th>
                <th className="px-3 py-2 text-right">Mínimo</th>
                <th className="px-3 py-2 text-right">Consumo/dia</th>
                <th className="px-3 py-2 text-right">Cobertura</th>
                <th className="px-3 py-2 text-right">Lead</th>
                <th className="px-3 py-2 text-right">Sugerido</th>
                <th className="px-3 py-2 text-right">Preço</th>
                <th className="px-3 py-2 text-right">Valor</th>
                <th className="px-3 py-2 text-left">Fornecedor</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {linhas.isLoading && (
                <tr><td colSpan={14} className="text-center py-8 text-ink-500">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                </td></tr>
              )}
              {!linhas.isLoading && filtradas.length === 0 && (
                <tr><td colSpan={14} className="text-center py-8 text-ink-500">
                  Nenhuma sugestão encontrada para o filtro atual.
                </td></tr>
              )}
              {filtradas.map(l => {
                const Urg = URG_BADGE[l.urgencia]
                const Icon = Urg.icon
                const elegivel = !!l.fornecedor_sugerido && l.quantidade_sugerida > 0
                return (
                  <tr
                    key={l.id}
                    onClick={() => setShowDetalhe(l)}
                    className="border-b border-ink-100 hover:bg-violet-50 cursor-pointer"
                  >
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        disabled={!elegivel}
                        checked={selecionados.has(l.id)}
                        onChange={() => toggleSel(l.id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${Urg.cls}`}>
                        <Icon className="h-3 w-3" />
                        {Urg.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{l.codigo}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{l.descricao}</div>
                      {l.tipo === 'materia_prima' && l.categoria && (
                        <div className="text-xs text-ink-500">{l.categoria}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{l.estoque_atual.toLocaleString('pt-BR')} {l.unidade}</td>
                    <td className="px-3 py-2 text-right text-ink-500">
                      {l.estoque_minimo.toLocaleString('pt-BR')}
                      {l.estoque_minimo_estimado && (
                        <span className="ml-1 text-[10px] text-amber-600 font-semibold" title="Mínimo estimado pela média de saída do WinThor">~est</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">{l.consumo_diario.toLocaleString('pt-BR')}</td>
                    <td className="px-3 py-2 text-right">
                      {l.cobertura_dias != null ? (
                        <span className={l.cobertura_dias < l.lead_time_dias ? 'text-rose-700 font-semibold' : ''}>
                          {l.cobertura_dias.toFixed(1)} d
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{l.lead_time_dias} d</td>
                    <td className="px-3 py-2 text-right font-semibold">
                      {l.quantidade_sugerida > 0 ? l.quantidade_sugerida.toLocaleString('pt-BR') : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">{formatMoney(l.preco_estimado)}</td>
                    <td className="px-3 py-2 text-right font-medium">{formatMoney(l.valor_estimado)}</td>
                    <td className="px-3 py-2 text-xs">
                      {l.fornecedor_sugerido ? (
                        <div>
                          <div className="font-medium">{l.fornecedor_sugerido.fornecedor_nome || '—'}</div>
                          <div className="text-ink-500">Lead {l.fornecedor_sugerido.lead_time_dias}d</div>
                        </div>
                      ) : (
                        <span className="text-ink-400 italic">sem fornecedor</span>
                      )}
                    </td>
                    <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setShowDetalhe(l)}
                        className="text-ink-400 hover:text-violet-600"
                        title="Detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de detalhe */}
      {showDetalhe && (
        <DetalheModal linha={showDetalhe} onClose={() => setShowDetalhe(null)} />
      )}

      {/* Modal de parâmetros */}
      {showParams && params.data && (
        <ParametrosModal
          params={params.data}
          onClose={() => setShowParams(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['compras-parametros'] })
            qc.invalidateQueries({ queryKey: ['compras-sugestoes', modo] })
            qc.invalidateQueries({ queryKey: ['compras-sugestoes-kpis', modo] })
          }}
        />
      )}
    </div>
  )
}

function Kpi({ label, value, cls }: { label: string; value: string | number; cls: string }) {
  return (
    <div className={`rounded-lg border px-4 py-3 ${cls}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  )
}

function DetalheModal({ linha, onClose }: { linha: SugestaoLinha; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">{linha.descricao}</h2>
            <p className="text-xs font-mono text-ink-500">{linha.codigo} — {linha.tipo === 'produto' ? 'Produto' : 'Matéria-prima'}</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div className="grid grid-cols-3 gap-3">
            <Info label="Estoque atual" value={`${linha.estoque_atual.toLocaleString('pt-BR')} ${linha.unidade}`} />
            <Info label="Estoque mínimo" value={linha.estoque_minimo.toLocaleString('pt-BR')} />
            <Info label="Consumo médio/dia" value={linha.consumo_diario.toLocaleString('pt-BR')} />
            <Info label="Consumo médio/mês" value={linha.consumo_mensal.toLocaleString('pt-BR')} />
            <Info label="Cobertura atual" value={linha.cobertura_dias != null ? `${linha.cobertura_dias.toFixed(1)} dias` : '—'} />
            <Info label="Lead time" value={`${linha.lead_time_dias} dias`} />
            <Info label="Ponto de pedido" value={linha.ponto_pedido.toLocaleString('pt-BR')} />
            <Info label="Quantidade sugerida" value={`${linha.quantidade_sugerida.toLocaleString('pt-BR')} ${linha.unidade}`} />
            <Info label="Valor estimado" value={formatMoney(linha.valor_estimado)} />
          </div>
          {linha.fornecedor_sugerido && (
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
              <div className="text-xs uppercase text-violet-700 font-semibold">Fornecedor sugerido</div>
              <div className="font-semibold">{linha.fornecedor_sugerido.fornecedor_nome}</div>
              <div className="text-xs text-ink-600 mt-1">
                Última compra: {formatMoney(linha.fornecedor_sugerido.preco_ultima_compra)} —
                Lead {linha.fornecedor_sugerido.lead_time_dias} dias
              </div>
            </div>
          )}
          {linha.modo === 'mrp' && linha.contribuicoes && linha.contribuicoes.length > 0 && (
            <div>
              <div className="text-xs uppercase text-ink-500 font-semibold mb-2">
                Demanda projetada ({linha.horizonte_dias} dias) — produtos finais que consomem esta MP
              </div>
              <table className="w-full text-xs border border-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-2 py-1 text-left">Produto</th>
                    <th className="px-2 py-1 text-right">Vendas/dia</th>
                    <th className="px-2 py-1 text-right">MP/un (com perda)</th>
                    <th className="px-2 py-1 text-right">Demanda no horizonte</th>
                  </tr>
                </thead>
                <tbody>
                  {linha.contribuicoes.map((c, i) => (
                    <tr key={i} className="border-t border-ink-100">
                      <td className="px-2 py-1">#{c.produto_id}</td>
                      <td className="px-2 py-1 text-right">{c.vendas_diarias.toLocaleString('pt-BR')}</td>
                      <td className="px-2 py-1 text-right">{c.qty_por_unidade.toLocaleString('pt-BR')}</td>
                      <td className="px-2 py-1 text-right font-medium">{c.demanda_horizonte.toLocaleString('pt-BR')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-ink-500 font-medium">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  )
}

function ParametrosModal({
  params, onClose, onSaved,
}: { params: Parametros; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    janela_consumo_dias: params.janela_consumo_dias,
    cobertura_alvo_dias: params.cobertura_alvo_dias,
    buffer_lead_time_dias: params.buffer_lead_time_dias,
    limite_aprovacao_bpm: Number(params.limite_aprovacao_bpm),
  })
  const m = useMutation({
    mutationFn: () => api.put('/compras/parametros', form).then(r => r.data),
    onSuccess: () => {
      toast.success('Parâmetros atualizados')
      onSaved()
      onClose()
    },
    onError: () => toast.error('Falha ao salvar'),
  })
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Parâmetros de Compras Inteligente</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <Field label="Janela de consumo (dias)" hint="Quantos dias considerar pra calcular giro médio">
            <input
              type="number" min={7} max={720}
              value={form.janela_consumo_dias}
              onChange={e => setForm({ ...form, janela_consumo_dias: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-ink-300 rounded-lg"
            />
          </Field>
          <Field label="Cobertura alvo (dias)" hint="Quanto estoque a sugestão deve cobrir após chegar">
            <input
              type="number" min={7} max={365}
              value={form.cobertura_alvo_dias}
              onChange={e => setForm({ ...form, cobertura_alvo_dias: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-ink-300 rounded-lg"
            />
          </Field>
          <Field label="Buffer lead time (dias)" hint="Segurança somada ao lead time pra disparar alerta">
            <input
              type="number" min={0} max={60}
              value={form.buffer_lead_time_dias}
              onChange={e => setForm({ ...form, buffer_lead_time_dias: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-ink-300 rounded-lg"
            />
          </Field>
          <Field label="Limite de aprovação BPM (R$)" hint="Pedidos acima deste valor disparam aprovação no BPM">
            <input
              type="number" min={0}
              value={form.limite_aprovacao_bpm}
              onChange={e => setForm({ ...form, limite_aprovacao_bpm: Number(e.target.value) })}
              className="w-full px-3 py-2 border border-ink-300 rounded-lg"
            />
          </Field>
          <div className="pt-3 flex justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-ink-300 rounded-lg text-sm">Cancelar</button>
            <button
              onClick={() => m.mutate()}
              disabled={m.isPending}
              className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm flex items-center gap-2"
            >
              {m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase text-ink-600 font-medium mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-ink-500 mt-1">{hint}</p>}
    </div>
  )
}
